import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Modal form to create one or more evaluation assignments.
 * Wires real backend: GET /api/admin/clients, /api/admin/questionnaires,
 * /api/admin/companies/{id}/employees, POST /api/admin/assignments.
 */
export function NewEvaluationDialog({
  open,
  onOpenChange,
  defaultCompanyId,
  defaultQuestionnaireId,
  defaultQuestionnaireLabel,  // UAT #2C — display label for locked mode
  defaultSuiteCode,
  defaultConfigurationId,   // Phase 6 — preselect a configuration (assign-from-row)
  defaultPositionId,        // Iter 41.2 — set by "Test this position"
  defaultFitThreshold,      // Iter 41.2 — pre-set from the position profile
  onCreated,
}) {
  const { t } = useTranslation();

  // UAT #2C — when opened from a specific questionnaire, the evaluation is
  // pre-filled and locked; the user only picks client, participants, date.
  const locked = !!defaultQuestionnaireId;

  // form data
  const [companies, setCompanies] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bootLoading, setBootLoading] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [companyId, setCompanyId] = useState('');
  const [suiteCode, setSuiteCode] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [participantIds, setParticipantIds] = useState([]);
  const [deadline, setDeadline] = useState('');
  // Phase 6 — configuration pinning
  const [configs, setConfigs] = useState([]);
  const [configurationId, setConfigurationId] = useState('');
  const [cfgLoading, setCfgLoading] = useState(false);

  // Track whether the initial pre-fill has settled. Cascade resets are
  // suppressed during initial pre-fill so an externally-provided
  // (defaultSuiteCode / defaultQuestionnaireId) is not wiped.
  const initialised = useRef(false);

  // boot data when dialog opens
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBootLoading(true);
    Promise.all([adminApi.listCompanies(), adminApi.listQuestionnaires()])
      .then(([cos, qs]) => {
        if (!alive) return;
        setCompanies(cos);
        // Library MVP — only published evaluations are assignable.
        setQuestionnaires((qs || []).filter((q) => q.status === 'published' && q.current_version_id));
        if (defaultCompanyId) setCompanyId(defaultCompanyId);
        if (defaultSuiteCode) setSuiteCode(defaultSuiteCode);
        if (defaultQuestionnaireId) {
          setQuestionnaireId(defaultQuestionnaireId);
          if (!defaultSuiteCode) {
            const found = (qs || []).find((q) => q.id === defaultQuestionnaireId);
            // Internal-library assessments have no suite — use the pseudo-suite.
            setSuiteCode(found?.suite_codes?.length ? found.suite_codes[0] : INTERNAL_LIBRARY);
          }
        }
        // mark initialised AFTER state is applied — the cascade effects
        // run on the next render and will then be allowed to reset.
        setTimeout(() => { initialised.current = true; }, 0);
      })
      .catch((e) => toast.error(t('newEval.loadFailed'), { description: formatApiError(e) }))
      .finally(() => { if (alive) setBootLoading(false); });
    return () => { alive = false; };
  }, [open, defaultCompanyId, defaultSuiteCode, defaultQuestionnaireId, t]);

  // load employees when company changes
  useEffect(() => {
    if (!companyId) { setEmployees([]); setParticipantIds([]); return; }
    let alive = true;
    setEmpLoading(true);
    adminApi.listCompanyEmployees(companyId)
      .then((emps) => { if (alive) setEmployees(emps || []); })
      .catch(() => { if (alive) setEmployees([]); })
      .finally(() => { if (alive) setEmpLoading(false); });
    return () => { alive = false; };
  }, [companyId]);

  // UAT Option 2 (founder-approved) — pseudo-suite exposing published
  // assessments that belong to no suite (publish_target internal_library).
  // Does NOT alter the suite-first flow or the anti-pollution filter.
  const INTERNAL_LIBRARY = '__internal_library__';
  const isInternalOnly = (q) =>
    q.publish_target === 'internal_library' || (q.suite_codes || []).length === 0;

  // available suites for this client = suite codes covered by an active questionnaire
  const availableSuites = useMemo(() => {
    const codes = new Set();
    questionnaires.forEach((q) => (q.suite_codes || []).forEach((c) => codes.add(c)));
    return Array.from(codes);
  }, [questionnaires]);

  // questionnaire options filtered by suite
  // FIX 1 (Iter 41.15) — require a suite first; never show questionnaires
  // from other suites when a suite is selected. Empty `suite_codes` entries
  // (test fixtures) are excluded by the `.includes()` check.
  const filteredQs = useMemo(
    () => {
      if (suiteCode === INTERNAL_LIBRARY) return questionnaires.filter(isInternalOnly);
      return suiteCode ? questionnaires.filter((q) => (q.suite_codes || []).includes(suiteCode)) : [];
    },
    [questionnaires, suiteCode],
  );

  // Phase 6 — load ACTIVE configurations of the selected assessment; exclusive
  // configurations are invisible outside their suites (spec decision #5).
  useEffect(() => {
    if (!open || !questionnaireId) { setConfigs([]); setConfigurationId(''); return undefined; }
    let alive = true;
    setCfgLoading(true);
    adminApi.listAssessmentConfigs(questionnaireId)
      .then((all) => {
        if (!alive) return;
        const suite = suiteCode === INTERNAL_LIBRARY ? null : suiteCode;
        const active = (all || []).filter((c) => c.status === 'active').filter((c) => {
          const sc = c.suite_compatibility || {};
          if (sc.mode !== 'exclusive') return true;
          return suite ? (sc.suites || []).includes(suite) : false;
        });
        setConfigs(active);
        if (defaultConfigurationId && active.some((c) => c.id === defaultConfigurationId)) {
          setConfigurationId(defaultConfigurationId);
        } else if (active.length === 1) {
          setConfigurationId(active[0].id);
        } else {
          setConfigurationId(active.find((c) => c.is_default)?.id || '');
        }
      })
      .catch(() => { if (alive) { setConfigs([]); setConfigurationId(''); } })
      .finally(() => { if (alive) setCfgLoading(false); });
    return () => { alive = false; };
  }, [open, questionnaireId, suiteCode, defaultConfigurationId]);


  // UAT #2C — locked mode always shows exactly the pre-selected evaluation.
  const qOptions = useMemo(() => {
    if (!locked) return filteredQs;
    const found = questionnaires.find((q) => q.id === defaultQuestionnaireId);
    if (found) return [found];
    return [{ id: defaultQuestionnaireId, name: defaultQuestionnaireLabel || '—', code: '' }];
  }, [locked, filteredQs, questionnaires, defaultQuestionnaireId, defaultQuestionnaireLabel]);

  // reset cascading fields (skip while initial defaults are still settling;
  // UAT #2C — never wipe a locked pre-filled evaluation when the user picks
  // the client or suite afterwards)
  useEffect(() => {
    if (!initialised.current || locked) return;
    setQuestionnaireId('');
  }, [suiteCode, locked]);
  useEffect(() => {
    if (!initialised.current) return;
    if (!locked) { setSuiteCode(''); setQuestionnaireId(''); }
    setParticipantIds([]);
  }, [companyId, locked]);

  const reset = () => {
    setCompanyId(defaultCompanyId || '');
    setSuiteCode(defaultSuiteCode || '');
    setQuestionnaireId(defaultQuestionnaireId || '');
    setParticipantIds([]);
    setDeadline('');
    setConfigs([]);
    setConfigurationId('');
  };

  const close = () => {
    if (submitting) return;
    onOpenChange(false);
    initialised.current = false;
    setTimeout(reset, 200);
  };

  const toggleParticipant = (uid) => {
    setParticipantIds((prev) => prev.includes(uid) ? prev.filter((p) => p !== uid) : [...prev, uid]);
  };
  const [participantQuery, setParticipantQuery] = useState('');

  const handleSubmit = async () => {
    if (!companyId) { toast.error(t('newEval.validationClient')); return; }
    if (!suiteCode) { toast.error(t('newEval.validationSuite')); return; }
    if (!questionnaireId) { toast.error(t('newEval.validationQuestionnaire')); return; }
    if (!cfgLoading && configs.length === 0) { toast.error(t('newEval.noActiveConfig')); return; }
    if (!configurationId) { toast.error(t('newEval.validationConfiguration')); return; }
    if (participantIds.length === 0) { toast.error(t('newEval.validationParticipants')); return; }

    setSubmitting(true);
    let ok = 0;
    let fail = 0;
    const failures = [];
    for (const uid of participantIds) {
      try {
        await adminApi.createAssignment({
          questionnaire_id: questionnaireId,
          assigned_to: uid,
          configuration_id: configurationId,
          // Pseudo-suite is UI-only: internal-library assignments carry no suite.
          suite_code: suiteCode === INTERNAL_LIBRARY ? null : suiteCode,
          deadline: deadline ? new Date(deadline).toISOString() : null,
          // Iter 41.2 — carry position context when launched via "Test this position".
          ...(defaultPositionId ? { position_id: defaultPositionId } : {}),
          ...(defaultFitThreshold ? { fit_threshold: defaultFitThreshold } : {}),
        });
        ok += 1;
      } catch (e) {
        fail += 1;
        failures.push(formatApiError(e));
      }
    }
    setSubmitting(false);

    if (ok > 0 && fail === 0) {
      toast.success(t('newEval.successAll', { count: ok }));
    } else if (ok > 0 && fail > 0) {
      toast.warning(t('newEval.successPartial', { ok, fail }), {
        description: failures[0],
      });
    } else {
      toast.error(t('newEval.failAll'), { description: failures[0] });
    }

    if (ok > 0) {
      onCreated && onCreated({ ok, fail });
      close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-xl lg:max-w-4xl w-[92vw] max-h-[88vh] overflow-y-auto" data-testid="new-eval-dialog">
        <DialogHeader>
          <DialogTitle>{t('newEval.title')}</DialogTitle>
          <DialogDescription>{t('newEval.subtitle')}</DialogDescription>
        </DialogHeader>

        {bootLoading ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 py-1">
            {/* Client */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t('newEval.client')}</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="new-eval-client"><SelectValue placeholder={t('newEval.clientPh')} /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Suite */}
            <div className="space-y-1.5">
              <Label>{t('newEval.suite')}</Label>
              <Select value={suiteCode} onValueChange={setSuiteCode} disabled={locked || !companyId}>
                <SelectTrigger data-testid="new-eval-suite"><SelectValue placeholder={t('newEval.suitePh')} /></SelectTrigger>
                <SelectContent>
                  {availableSuites.map((code) => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                  <SelectItem value={INTERNAL_LIBRARY} data-testid="new-eval-suite-internal-library">
                    {t('newEval.internalLibrary', { defaultValue: 'Biblioteca Interna' })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Questionnaire */}
            <div className="space-y-1.5">
              <Label>{t('newEval.questionnaire')}</Label>
              <Select value={questionnaireId} onValueChange={setQuestionnaireId} disabled={locked || !suiteCode}>
                <SelectTrigger data-testid="new-eval-questionnaire"><SelectValue placeholder={t('newEval.questionnairePh')} /></SelectTrigger>
                <SelectContent>
                  {qOptions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">{t('newEval.questionnaireEmpty')}</div>
                  ) : qOptions.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.name}
                      <span className="ml-1.5 text-xs text-muted-foreground font-mono">{q.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locked && (
                <p className="text-xs text-muted-foreground" data-testid="new-eval-locked-hint">
                  {t('newEval.lockedHint', { defaultValue: 'Pre-selected from the evaluation you opened.' })}
                </p>
              )}
            </div>

            {/* Sprint A — Objective + Instructions preview (read-only). */}
            {(() => {
              const selected = questionnaires.find((q) => q.id === questionnaireId);
              if (!selected || (!selected.objective && !selected.instructions)) return null;
              return (
                <div className="sm:col-span-2 space-y-2 rounded-xl border border-border/70 bg-muted/30 p-3" data-testid="new-eval-context">
                  {selected.objective && (
                    <div>
                      <p className="text-xs uppercase tracking-wider font-semibold text-primary">
                        {t('library.metadata.objective.label', { defaultValue: 'Objective' })}
                      </p>
                      <p className="text-xs text-foreground/80 leading-relaxed mt-0.5">{selected.objective}</p>
                    </div>
                  )}
                  {selected.instructions && (
                    <div>
                      <p className="text-xs uppercase tracking-wider font-semibold text-accent">
                        {t('library.metadata.instructions.label', { defaultValue: 'Instructions' })}
                      </p>
                      <p className="text-xs text-foreground/80 leading-relaxed mt-0.5">{selected.instructions}</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Phase 6 — Configuration selection (pinned at assignment time) */}
            {questionnaireId && (
              <div className="sm:col-span-2 space-y-1.5" data-testid="new-eval-config-block">
                <Label>{t('newEval.configuration', { defaultValue: 'Configuración' })}</Label>
                {cfgLoading ? (
                  <div className="flex items-center text-xs text-muted-foreground py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> {t('common.loading')}
                  </div>
                ) : configs.length === 0 ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive" data-testid="new-eval-config-blocked">
                    {t('newEval.noActiveConfig')}
                  </div>
                ) : configs.length === 1 ? (
                  <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="new-eval-config-single">
                    <ConfigSummary c={configs[0]} t={t} />
                  </div>
                ) : (
                  <div className="rounded-lg border border-border divide-y divide-border max-h-44 overflow-y-auto" data-testid="new-eval-config-options">
                    {configs.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40">
                        <input
                          type="radio"
                          name="new-eval-config"
                          checked={configurationId === c.id}
                          onChange={() => setConfigurationId(c.id)}
                          data-testid={`new-eval-config-${c.id}`}
                        />
                        <ConfigSummary c={c} t={t} />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Deadline */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t('newEval.deadline')}</Label>
              <Input type="date" data-testid="new-eval-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>

            {/* Participants */}
            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>{t('newEval.participants')}</Label>
                {participantIds.length > 0 && (
                  <span className="text-xs text-muted-foreground tabular-nums">{t('newEval.selected', { count: participantIds.length })}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('newEval.participantsHint')}</p>
              <Input
                value={participantQuery}
                onChange={(e) => setParticipantQuery(e.target.value)}
                placeholder={t('newEval.participantSearch', { defaultValue: 'Buscar por nombre o email…' })}
                className="h-8"
                data-testid="new-eval-participant-search"
              />
              <div className="rounded-lg border border-border max-h-52 lg:max-h-[38vh] overflow-y-auto divide-y divide-border" data-testid="new-eval-participants">
                {!companyId ? (
                  <div className="p-4 text-sm text-muted-foreground text-center">{t('newEval.clientPh')}</div>
                ) : empLoading ? (
                  <div className="p-4 flex items-center justify-center text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
                  </div>
                ) : employees.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground text-center">{t('newEval.participantsEmpty')}</div>
                ) : (() => {
                  const q = participantQuery.trim().toLowerCase();
                  const visible = q
                    ? employees.filter((e) => `${e.name || ''} ${e.email || ''}`.toLowerCase().includes(q))
                    : employees;
                  if (!visible.length) {
                    return <div className="p-4 text-sm text-muted-foreground text-center" data-testid="new-eval-participants-no-match">{t('newEval.noMatch', { defaultValue: 'Sin coincidencias para la búsqueda.' })}</div>;
                  }
                  return visible.map((e) => (
                  <label
                    key={e.id}
                    htmlFor={`emp-${e.id}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer"
                  >
                    <Checkbox
                      id={`emp-${e.id}`}
                      checked={participantIds.includes(e.id)}
                      onCheckedChange={() => toggleParticipant(e.id)}
                      data-testid={`new-eval-emp-${e.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{e.email}</p>
                    </div>
                  </label>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>{t('common.cancel')}</Button>
          <Button
            data-testid="new-eval-submit"
            onClick={handleSubmit}
            disabled={submitting || bootLoading || (!!questionnaireId && !cfgLoading && configs.length === 0)}
            className="bg-gradient-primary hover:opacity-90"
          >
            {submitting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('newEval.creating')}</>) : t('newEval.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NewEvaluationDialog;

function ConfigSummary({ c, t }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-semibold">{c.label}</span>
        {c.is_default && (
          <span className="text-xs px-1.5 py-0.5 rounded border border-warning/40 text-warning" data-testid={`cfg-default-badge-${c.id}`}>
            {t('newEval.defaultBadge', { defaultValue: 'predeterminada' })}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">
        {(c.language || '').toUpperCase()}{c.level ? ` · ${c.level}` : ''} · v{c.semver} · {c.report_template}
      </p>
    </div>
  );
}
